import { StateCreator } from 'zustand';
import { CartStore, ImmediateCartSlice } from './types';

export const createImmediateCartSlice: StateCreator<CartStore, [], [], ImmediateCartSlice> = (set, get) => ({
    items: [],
    offers: [],
    notes: '',
    idempotencyKey: null,

    regenerateIdempotencyKey: () => {
        set({ idempotencyKey: crypto.randomUUID() });
    },

    addItem: (product, quantity = 1, options) => {
        const items = get().items;
        const observation = options?.observation || '';
        const sub_products = options?.sub_products || [];
        
        // Generate unique ID for this specific product + sub-product combination
        const cartItemId = sub_products.length > 0 
            ? `${product.uuid}|${sub_products.map(s => s.sub_product_uuid).sort().join(',')}` 
            : product.uuid;
            
        const existingItem = items.find(item => item.cartItemId === cartItemId);

        if (existingItem) {
            set({
                items: items.map(item =>
                    item.cartItemId === cartItemId
                        ? { ...item, quantity: item.quantity + quantity, observation: observation || item.observation }
                        : item
                ),
            });
        } else {
            set({ items: [...items, { cartItemId, product, quantity, type: 'product', observation, sub_products }] });
        }
        get().regenerateIdempotencyKey();
    },

    addOffer: (offer, quantity = 1) => {
        const offers = get().offers;
        const existingOffer = offers.find(item => item.offer.uuid === offer.uuid);

        if (existingOffer) {
            set({
                offers: offers.map(item =>
                    item.offer.uuid === offer.uuid
                        ? { ...item, quantity: item.quantity + quantity }
                        : item
                ),
            });
        } else {
            set({
                offers: [...offers, { offer, quantity, type: 'offer' }],
            });
        }
        get().regenerateIdempotencyKey();
    },

    removeItem: (cartItemId) => {
        set({
            items: get().items.filter(item => item.cartItemId !== cartItemId),
        });
        get().regenerateIdempotencyKey();
    },

    removeOffer: (offerUuid) => {
        set({
            offers: get().offers.filter(item => item.offer.uuid !== offerUuid),
        });
        get().regenerateIdempotencyKey();
    },

    updateQuantity: (cartItemId, quantity) => {
        if (quantity <= 0) {
            get().removeItem(cartItemId);
            return;
        }

        set({
            items: get().items.map(item =>
                item.cartItemId === cartItemId
                    ? { ...item, quantity }
                    : item
            ),
        });
        get().regenerateIdempotencyKey();
    },

    updateOfferQuantity: (offerUuid, quantity) => {
        if (quantity <= 0) {
            get().removeOffer(offerUuid);
            return;
        }

        set({
            offers: get().offers.map(item =>
                item.offer.uuid === offerUuid
                    ? { ...item, quantity }
                    : item
            ),
        });
        get().regenerateIdempotencyKey();
    },

    updateItemObservation: (cartItemId, observation) => {
        set({
            items: get().items.map(item =>
                item.cartItemId === cartItemId ? { ...item, observation } : item
            ),
        });
        get().regenerateIdempotencyKey();
    },

    updateItemSubProducts: (cartItemId, sub_products) => {
        // When sub-products change, the cartItemId must also change
        const items = get().items;
        const itemToUpdate = items.find(i => i.cartItemId === cartItemId);
        if (!itemToUpdate) return;

        const newCartItemId = sub_products.length > 0 
            ? `${itemToUpdate.product.uuid}|${sub_products.map(s => s.sub_product_uuid).sort().join(',')}` 
            : itemToUpdate.product.uuid;

        // Check if an item with the NEW cartItemId already exists
        const existingWithNewId = items.find(i => i.cartItemId === newCartItemId && i.cartItemId !== cartItemId);

        if (existingWithNewId) {
            // Merge them
            set({
                items: items
                    .filter(i => i.cartItemId !== cartItemId)
                    .map(i => i.cartItemId === newCartItemId 
                        ? { ...i, quantity: i.quantity + itemToUpdate.quantity, sub_products } 
                        : i)
            });
        } else {
            // Just update ID and sub_products
            set({
                items: items.map(item =>
                    item.cartItemId === cartItemId ? { ...item, cartItemId: newCartItemId, sub_products } : item
                ),
            });
        }
        get().regenerateIdempotencyKey();
    },

    updateOfferObservation: (offerUuid, observation) => {
        set({
            offers: get().offers.map(item =>
                item.offer.uuid === offerUuid
                    ? { ...item, observation }
                    : item
            ),
        });
        get().regenerateIdempotencyKey();
    },

    setNotes: (notes) => {
        set({ notes });
        get().regenerateIdempotencyKey();
    },

    clearCart: () => {
        set({ items: [], offers: [], notes: '', idempotencyKey: null });
    },

    getSubtotal: () => {
        const items = get().items;
        const offers = get().offers;

        const itemsTotal = items.reduce((sum, item) => {
            const productPrice = item.product.price;
            const subProductsPrice = item.sub_products?.reduce((acc, sub) => acc + sub.additional_price, 0) || 0;
            return sum + ((productPrice + subProductsPrice) * item.quantity);
        }, 0);

        const offersTotal = offers.reduce((sum, item) => sum + (item.offer.price * item.quantity), 0);

        return itemsTotal + offersTotal;
    },

    getService: () => {
        return get().getSubtotal() * 0; // 10% service charge placeholder
    },

    hasImmediateOrders: () => {
        return get().items.length > 0 || get().offers.length > 0;
    },
});
